import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Mail,
  MessageSquare,
  Plus,
  Send,
  Loader2,
  CheckCircle,
  Eye,
  Users,
  ChevronRight,
  ArrowLeft,
  TestTube,
  Trash2,
  RotateCcw,
  Upload,
  X,
  AlertTriangle,
  Pencil,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: "Setup", icon: Mail },
  { id: 2, name: "Template", icon: MessageSquare },
  { id: 3, name: "Preview", icon: Eye },
  { id: 4, name: "Test", icon: TestTube },
  { id: 5, name: "Send", icon: Send },
];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { valid: [], error: "CSV has no data rows" };

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const phoneCol = headers.indexOf("phone_number");
  if (phoneCol === -1) return { valid: [], error: 'CSV must have a "phone_number" header column' };

  const valid = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",");
    const val = (cols[phoneCol] || "").trim().replace(/\s+/g, "");
    if (val) valid.push(val);
  }
  return { valid, error: null };
}

export const AdminCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [recipientCount, setRecipientCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [testContact, setTestContact] = useState("");
  const [singleContact, setSingleContact] = useState("");
  const [createdCampaign, setCreatedCampaign] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", template: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // custom numbers state
  const [manualNumbers, setManualNumbers] = useState("");
  const [csvNumbers, setCsvNumbers] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const csvInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    campaign_type: "sms",
    subject: "",
    template: "",
    recipient_filter: { type: "all_customers" },
  });

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/admin/campaigns`);
      setCampaigns(res.data.campaigns);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientCount = async (filterType, campaignType = formData.campaign_type, contact = "") => {
    try {
      let url = `${API}/admin/campaigns/recipients/count?filter_type=${filterType}&campaign_type=${campaignType}`;
      if (filterType === "single_contact" && contact) url += `&contact=${encodeURIComponent(contact)}`;
      const res = await axios.get(url);
      setRecipientCount(res.data.count);
    } catch {
      // silent
    }
  };

  const allCustomNumbers = () => {
    const manual = manualNumbers
      .split(/[\n,]+/)
      .map((n) => n.trim())
      .filter(Boolean);
    return [...new Set([...manual, ...csvNumbers])];
  };

  const handleFilterChange = (type) => {
    setSingleContact("");
    setFormData({ ...formData, recipient_filter: { type } });
    if (type === "custom") {
      setRecipientCount(allCustomNumbers().length);
    } else if (type !== "single_contact") {
      fetchRecipientCount(type, formData.campaign_type);
    } else {
      setRecipientCount(0);
    }
  };

  const handleSingleContactChange = (value) => {
    setSingleContact(value);
    setFormData((prev) => ({ ...prev, recipient_filter: { type: "single_contact", contact: value } }));
    setRecipientCount(value.trim() ? 1 : 0);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvError("");
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { valid, error } = parseCSV(ev.target.result);
      if (error) {
        setCsvError(error);
        setCsvNumbers([]);
      } else {
        setCsvNumbers(valid);
        toast.success(`${valid.length} numbers loaded from CSV`);
      }
    };
    reader.readAsText(file);
  };

  const handleCreateCampaign = async () => {
    setSubmitting(true);
    try {
      let filter = { ...formData.recipient_filter };
      if (filter.type === "custom") {
        filter.numbers = allCustomNumbers();
      }
      const payload = { ...formData, recipient_filter: filter };
      const res = await axios.post(`${API}/admin/campaigns`, payload);
      setCreatedCampaign(res.data);
      setCurrentStep(3);
      toast.success("Campaign created!");
    } catch {
      toast.error("Failed to create campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestSend = async () => {
    if (!testContact) {
      toast.error("Please enter a test phone number");
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams({ test_contact: testContact });
      await axios.post(`${API}/admin/campaigns/${createdCampaign.id}/test?${params.toString()}`);
      toast.success(`Test sent to ${testContact}`);
      setCurrentStep(5);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to send test");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCampaign = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/campaigns/${createdCampaign.id}/send`);
      toast.success("Campaign sent!");
      setDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/campaigns/${deleteTarget.id}`);
      toast.success("Campaign deleted");
      setCampaigns((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (campaign) => {
    setEditTarget(campaign);
    setEditForm({ name: campaign.name, template: campaign.template || "" });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSubmitting(true);
    try {
      await axios.put(`${API}/admin/campaigns/${editTarget.id}`, editForm);
      setCampaigns((prev) => prev.map((c) => c.id === editTarget.id ? { ...c, ...editForm } : c));
      toast.success("Campaign updated");
      setEditTarget(null);
    } catch {
      toast.error("Failed to update campaign");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleResend = async (campaign) => {
    try {
      await axios.post(`${API}/admin/campaigns/${campaign.id}/resend`);
      const updated = await axios.get(`${API}/admin/campaigns`);
      setCampaigns(updated.data.campaigns);
      toast.success("Campaign reset — open it to send again");
    } catch {
      toast.error("Failed to reset campaign");
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      name: "",
      campaign_type: "sms",
      subject: "",
      template: "",
      recipient_filter: { type: "all_customers" },
    });
    setCreatedCampaign(null);
    setTestContact("");
    setSingleContact("");
    setManualNumbers("");
    setCsvNumbers([]);
    setCsvError("");
    setCsvFileName("");
    setRecipientCount(0);
  };

  const statusColors = {
    draft: "bg-neutral-500/20 text-neutral-400",
    scheduled: "bg-blue-500/20 text-blue-400",
    sending: "bg-yellow-500/20 text-yellow-400",
    completed: "bg-green-500/20 text-green-400",
    failed: "bg-red-500/20 text-red-400",
  };

  const customCount = allCustomNumbers().length;

  return (
    <div data-testid="admin-campaigns">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Campaigns</h1>
          <p className="text-neutral-400">Send SMS campaigns to your customers</p>
        </div>
        <Button className="btn-empire" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{campaigns.length}</div>
            <p className="text-sm text-neutral-400">Total Campaigns</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {campaigns.filter((c) => c.status === "completed").length}
            </div>
            <p className="text-sm text-neutral-400">Completed</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              {campaigns.reduce((sum, c) => sum + (c.sent_count || 0), 0)}
            </div>
            <p className="text-sm text-neutral-400">Total Sent</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-400">
              {campaigns.reduce((sum, c) => sum + (c.failed_count || 0), 0)}
            </div>
            <p className="text-sm text-neutral-400">Failed</p>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      <Card className="card-empire">
        <CardHeader>
          <CardTitle className="text-white">All Campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No campaigns yet. Create your first campaign!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-neutral-900/50 rounded-xl border border-neutral-800 gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-500/20 shrink-0">
                      <MessageSquare className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{campaign.name}</h4>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {campaign.created_at && (
                          <span className="text-xs text-neutral-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Created {format(parseISO(campaign.created_at), "MMM d, yyyy HH:mm")}
                          </span>
                        )}
                        {campaign.sent_at && (
                          <span className="text-xs text-empire-gold/70 flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            Sent {format(parseISO(campaign.sent_at), "MMM d, yyyy HH:mm")}
                          </span>
                        )}
                      </div>
                      {campaign.provider_message && (
                        <p className="text-xs text-neutral-500 mt-0.5">{campaign.provider_message}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-center">
                      <p className="text-xs text-neutral-500">Recipients</p>
                      <p className="font-bold text-white">{campaign.total_recipients}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-neutral-500">Sent</p>
                      <p className="font-bold text-green-400">{campaign.sent_count || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-neutral-500">Failed</p>
                      <p className="font-bold text-red-400">{campaign.failed_count || 0}</p>
                    </div>
                    <Badge className={statusColors[campaign.status]}>{campaign.status}</Badge>
                    <div className="flex gap-2 ml-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-neutral-700 text-neutral-300 h-8 px-2"
                        onClick={() => openEdit(campaign)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {(campaign.status === "completed" || campaign.status === "failed") && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-neutral-700 text-neutral-300 h-8 px-2"
                          onClick={() => handleResend(campaign)}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Resend
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-900/50 text-red-400 hover:bg-red-900/20 h-8 px-2"
                        onClick={() => setDeleteTarget(campaign)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create Campaign</DialogTitle>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isActive ? "bg-empire-gold/20 text-empire-gold" : isCompleted ? "text-green-400" : "text-neutral-500"
                  }`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    <span className="text-sm font-medium hidden sm:inline">{step.name}</span>
                  </div>
                  {index < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-neutral-600 mx-1" />}
                </div>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Setup */}
            {currentStep === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., January Promotions"
                    className="input-empire"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Campaign Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* SMS — active */}
                    <button
                      onClick={() => setFormData((prev) => ({ ...prev, campaign_type: "sms" }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.campaign_type === "sms"
                          ? "border-empire-gold bg-empire-gold/10"
                          : "border-neutral-800 hover:border-neutral-700"
                      }`}
                    >
                      <MessageSquare className={`w-6 h-6 mb-2 ${formData.campaign_type === "sms" ? "text-empire-gold" : "text-neutral-400"}`} />
                      <p className="font-medium text-white">SMS</p>
                      <p className="text-xs text-neutral-500">Send via UjumbeSMS</p>
                    </button>

                    {/* Email — coming soon */}
                    <div className="relative">
                      <div className="p-4 rounded-xl border-2 border-neutral-800 text-left opacity-40 blur-[1px] pointer-events-none select-none">
                        <Mail className="w-6 h-6 mb-2 text-neutral-400" />
                        <p className="font-medium text-white">Email</p>
                        <p className="text-xs text-neutral-500">Send via Resend</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center rounded-xl">
                        <Badge className="bg-neutral-700 text-neutral-300 text-xs pointer-events-none">Coming Soon</Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Recipients</Label>
                  <Select value={formData.recipient_filter.type} onValueChange={handleFilterChange}>
                    <SelectTrigger className="bg-neutral-900 border-neutral-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_customers">All Customers</SelectItem>
                      <SelectItem value="class_attendees">Class Attendees</SelectItem>
                      <SelectItem value="event_attendees">Event Attendees</SelectItem>
                      <SelectItem value="package_holders">Active Package Holders</SelectItem>
                      <SelectItem value="single_contact">Single Number (Test)</SelectItem>
                      <SelectItem value="custom">Custom Numbers</SelectItem>
                    </SelectContent>
                  </Select>

                  {formData.recipient_filter.type === "single_contact" && (
                    <div className="pt-1">
                      <Input
                        value={singleContact}
                        onChange={(e) => handleSingleContactChange(e.target.value)}
                        placeholder="07XXXXXXXX"
                        className="input-empire"
                      />
                    </div>
                  )}

                  {formData.recipient_filter.type === "custom" && (
                    <div className="space-y-3 p-3 bg-neutral-800/50 rounded-xl border border-neutral-700">
                      <div className="space-y-1">
                        <Label className="text-neutral-300 text-sm">Enter numbers manually</Label>
                        <Textarea
                          value={manualNumbers}
                          onChange={(e) => {
                            setManualNumbers(e.target.value);
                            const nums = e.target.value.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean);
                            setRecipientCount(new Set([...nums, ...csvNumbers]).size);
                          }}
                          placeholder={"0712345678\n0723456789\n0734567890"}
                          className="input-empire min-h-[80px] text-sm font-mono"
                        />
                        <p className="text-xs text-neutral-500">One number per line, or comma-separated</p>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-neutral-300 text-sm">Or upload a CSV</Label>
                        <div
                          className="flex items-center gap-2 p-3 border border-dashed border-neutral-700 rounded-lg cursor-pointer hover:border-neutral-500 transition-colors"
                          onClick={() => csvInputRef.current?.click()}
                        >
                          <Upload className="w-4 h-4 text-neutral-400" />
                          <span className="text-sm text-neutral-400">
                            {csvFileName ? csvFileName : "Click to upload CSV"}
                          </span>
                          {csvFileName && (
                            <button
                              className="ml-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCsvNumbers([]);
                                setCsvFileName("");
                                setCsvError("");
                                if (csvInputRef.current) csvInputRef.current.value = "";
                              }}
                            >
                              <X className="w-3.5 h-3.5 text-neutral-500 hover:text-red-400" />
                            </button>
                          )}
                        </div>
                        <input
                          ref={csvInputRef}
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={handleCSVUpload}
                        />
                        {csvError && (
                          <p className="text-xs text-red-400 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {csvError}
                          </p>
                        )}
                        {!csvError && (
                          <p className="text-xs text-neutral-500">CSV must have a <code className="text-neutral-400">phone_number</code> column</p>
                        )}
                        {csvNumbers.length > 0 && (
                          <p className="text-xs text-green-400">{csvNumbers.length} numbers loaded from CSV</p>
                        )}
                      </div>
                    </div>
                  )}

                  {recipientCount > 0 && (
                    <p className="text-sm text-empire-gold flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={
                    !formData.name ||
                    (formData.recipient_filter.type === "single_contact" && !singleContact.trim()) ||
                    (formData.recipient_filter.type === "custom" && customCount === 0)
                  }
                  className="w-full btn-empire"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* Step 2: Template */}
            {currentStep === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Message *</Label>
                  <Textarea
                    value={formData.template}
                    onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                    placeholder="Write your SMS message here (160 chars recommended)..."
                    className="input-empire min-h-[200px]"
                  />
                  <p className="text-xs text-neutral-500">{formData.template.length}/160 characters</p>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1 border-neutral-700">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleCreateCampaign}
                    disabled={!formData.template || submitting}
                    className="flex-1 btn-empire"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Campaign"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Preview */}
            {currentStep === 3 && createdCampaign && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <Card className="bg-neutral-800 border-neutral-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge>SMS</Badge>
                      <span className="text-neutral-400">→</span>
                      <span className="text-white">{createdCampaign.total_recipients} recipients</span>
                    </div>
                    <div className="bg-neutral-900 p-4 rounded-lg">
                      <p className="text-neutral-300 whitespace-pre-wrap">{createdCampaign.template}</p>
                    </div>
                  </CardContent>
                </Card>

                <Button onClick={() => setCurrentStep(4)} className="w-full btn-empire">
                  Continue to Test <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* Step 4: Test */}
            {currentStep === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="text-center py-4">
                  <TestTube className="w-12 h-12 text-empire-gold mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">Send a Test First</h3>
                  <p className="text-neutral-400 text-sm">
                    Send to yourself before sending to all recipients.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Test Phone Number</Label>
                  <Input
                    value={testContact}
                    onChange={(e) => setTestContact(e.target.value)}
                    placeholder="07XXXXXXXX"
                    className="input-empire"
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentStep(5)} className="flex-1 border-neutral-700">
                    Skip Test
                  </Button>
                  <Button onClick={handleTestSend} disabled={!testContact || submitting} className="flex-1 btn-empire">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Test"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Send */}
            {currentStep === 5 && (
              <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="text-center py-4">
                  <Send className="w-12 h-12 text-empire-gold mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">Ready to Send!</h3>
                  <p className="text-neutral-400 text-sm">
                    This will send to <span className="text-empire-gold font-bold">{createdCampaign?.total_recipients || 0}</span> recipients.
                  </p>
                </div>

                <Card className="bg-neutral-800 border-neutral-700">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Campaign</span>
                      <span className="text-white">{createdCampaign?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Type</span>
                      <Badge>SMS</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Recipients</span>
                      <span className="text-empire-gold font-bold">{createdCampaign?.total_recipients}</span>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentStep(3)} className="flex-1 border-neutral-700">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button onClick={handleSendCampaign} disabled={submitting} className="flex-1 btn-empire">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Campaign</>}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Campaign Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                className="input-empire"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Message</Label>
              <Textarea
                value={editForm.template}
                onChange={(e) => setEditForm((f) => ({ ...f, template: e.target.value }))}
                className="input-empire min-h-[140px]"
              />
              <p className="text-xs text-neutral-500">{editForm.template.length}/160 characters</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditTarget(null)} className="flex-1 border-neutral-700">
                Cancel
              </Button>
              <Button
                onClick={handleEditSave}
                disabled={editSubmitting || !editForm.name || !editForm.template}
                className="flex-1 btn-empire"
              >
                {editSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Campaign</DialogTitle>
          </DialogHeader>
          <p className="text-neutral-400 text-sm">
            Delete <span className="text-white font-medium">{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1 border-neutral-700">
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
